import { useEffect, useState } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import KpiTile from "@/components/KpiTile";
import { fmtMoney, fmtNumber, fmtPercent, fmtMultiple } from "@/lib/format";
import ReconcileFlag, { findReconcile } from "@/components/ReconcileFlag";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, ArrowDownRight, ArrowUpRight, Info, ExternalLink, Clock, Check, X as XIcon, Users, Megaphone, Minus } from "lucide-react";
import {
  ComposedChart, Line, LineChart, Bar, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from "recharts";

/**
 * MomChip — matches the Dashboard's MoM chip styling: tinted background,
 * arrow icon, "MoM" label. `invert=true` for metrics where lower is better.
 */
function MomChip({ pct, invert = false, testid }) {
  if (pct === null || pct === undefined) return null;
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen bg-evergreen/10"
    : isBad ? "text-red-700 bg-red-700/10"
    : "text-midnight bg-midnight/10 opacity-70";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${color}`}
         data-testid={testid}>
      <span className="opacity-70">MoM</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </div>
  );
}

/**
 * YoyBadge — matches Dashboard: no background, colored text + "YoY" label.
 */
function YoyBadge({ pct, invert = false, testid }) {
  if (pct === null || pct === undefined) return null;
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen" : isBad ? "text-red-700" : "text-midnight opacity-60";
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  return (
    <div className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest ${color}`}
         data-testid={testid}>
      <span className="opacity-60 tracking-widest">YoY</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </div>
  );
}

const OWNER_STYLE = {
  marketing: { label: "Marketing", cls: "bg-cobalt text-warm", Icon: Megaphone },
  sales:     { label: "Sales",     cls: "bg-evergreen text-warm", Icon: Users },
};
function OwnerChip({ owner, testid }) {
  const s = OWNER_STYLE[owner] || OWNER_STYLE.marketing;
  const Icon = s.Icon;
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${s.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}

/**
 * OwnershipCallout — top-of-page banner. Reads the biggest_mover from the
 * sales-context endpoint and names the owner so Joey knows *who* to talk to.
 */
function OwnershipCallout({ salesCtx }) {
  if (!salesCtx || !salesCtx.biggest_mover) return null;
  const b = salesCtx.biggest_mover;
  const direction = (b.mom_pct || 0) >= 0 ? "up" : "down";
  const s = OWNER_STYLE[b.owner] || OWNER_STYLE.marketing;
  const shortMonth = (p) => {
    if (!p || p.length < 7) return p;
    const [y, m] = p.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
  };
  const Icon = s.Icon;
  return (
    <div className="bg-warm border-l-4 border-navy p-5 flex flex-wrap items-start justify-between gap-4"
         data-testid="ownership-callout">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 flex items-center justify-center ${s.cls} shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold mb-1">
            Where movement lives · month over month
          </div>
          <div className="text-lg font-bold text-navy">
            <span data-testid="ownership-metric">{b.label}</span> is{" "}
            <span className={direction === "up" ? "text-evergreen" : "text-red-700"}>
              {direction === "up" ? "up" : "down"} {Math.abs(b.mom_pct).toFixed(1)}%
            </span>{" "}
            in {shortMonth(b.current_period)} vs {shortMonth(b.prev_period)}.
          </div>
          <div className="text-xs text-midnight opacity-70 mt-1">
            This lever sits with <strong data-testid="ownership-owner">{s.label.toLowerCase()}</strong>. Booked-call volume is a Marketing signal; show-up, qualification, and close rates are Sales.
          </div>
        </div>
      </div>
      {b.yoy_available && b.yoy_pct !== null && (
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">vs {shortMonth(b.yoy_period)}</div>
          <YoyBadge pct={b.yoy_pct} testid="ownership-yoy" />
          <div className="text-[10px] opacity-60 mt-1">year over year</div>
        </div>
      )}
    </div>
  );
}

/**
 * SalesContextMini — one small line chart within the Sales-side context grid.
 * Extracted so React reconciler doesn't recreate the subtree on every render.
 */
function SalesContextMini({ label, dataKey, color, fmt, isRate, rows, shortMonth, delta }) {
  const vals = rows.map((r) => r[dataKey]).filter((v) => v != null);
  if (!vals.length) return null;
  const cur = vals[vals.length - 1];
  return (
    <div data-testid={`sales-context-${dataKey}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        {delta && <OwnerChip owner={delta.owner} testid={`owner-chip-${dataKey}`} />}
      </div>
      <div className="text-xl font-bold text-navy mb-1">{fmt(cur)}
        <span className="ml-2 text-[10px] uppercase tracking-widest opacity-60">
          {shortMonth(rows[rows.length - 1].period)}
        </span>
      </div>
      {delta && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <MomChip pct={delta.mom_pct} invert={delta.invert} testid={`mom-chip-${dataKey}`} />
          {delta.yoy_available
            ? <YoyBadge pct={delta.yoy_pct} invert={delta.invert} testid={`yoy-chip-${dataKey}`} />
            : <span className="text-[10px] uppercase tracking-widest opacity-40" data-testid={`yoy-unavailable-${dataKey}`}>YoY n/a</span>}
        </div>
      )}
      <ResponsiveContainer width="100%" height={70}>
        <LineChart data={rows} margin={{ top: 4, right: 6, bottom: 2, left: 2 }}>
          <XAxis dataKey="period" tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.6 }}
                 tickFormatter={shortMonth} axisLine={false} tickLine={false}
                 interval="preserveStartEnd" minTickGap={30} />
          <YAxis tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.6 }}
                 tickFormatter={(v) => isRate ? `${v}%` : v}
                 axisLine={false} tickLine={false} width={32}
                 domain={isRate ? [0, 100] : ["dataMin", "dataMax"]} />
          <Tooltip formatter={(v) => v == null ? "—" : fmt(v)} labelFormatter={shortMonth}
            contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
            itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.75}
                dot={false} connectNulls={true} isAnimationActive={false}
                activeDot={{ r: 3, fill: color, stroke: "#FFD100", strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * SalesContextTrends — 12-month line grid replacing the old static tiles.
 * Row 1: funnel counts (Leads · Held · Qualified · Won) — 4 mini line charts.
 * Row 2: rate metrics (Show-up % · Qual rate % · Close rate %) — 3 line charts.
 * Data comes from /api/analytics/sales-context-monthly.
 */
function SalesContextTrends({ data }) {
  if (!data) return <div className="text-xs opacity-60 italic">Loading history…</div>;
  const rows = data.rows || [];
  if (!rows.length) return <div className="text-xs opacity-60 italic">No history yet.</div>;
  const deltaByMetric = {};
  (data.deltas || []).forEach((d) => { deltaByMetric[d.metric] = d; });
  const shortMonth = (p) => {
    if (!p || p.length < 7) return p;
    const [y, m] = p.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
  };
  const counts = [
    { key: "leads",     label: "Total leads · calls booked", color: "#1D57A5" },
    { key: "held",      label: "Held (showed)",              color: "#1D57A5" },
    { key: "qualified", label: "Qualified",                  color: "#1D57A5" },
    { key: "won",       label: "Closed Won",                 color: "#006747" },
  ];
  const rates = [
    { key: "show_up_pct",    label: "Show-up rate · held ÷ booked",   color: "#1D57A5" },
    { key: "qual_rate_pct",  label: "Qualification rate · qual ÷ held", color: "#1D57A5" },
    { key: "close_rate_pct", label: "Close rate · won ÷ qualified",   color: "#006747" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {counts.map((c) => (
          <SalesContextMini key={c.key} label={c.label} dataKey={c.key} color={c.color}
                fmt={(v) => Math.round(v).toLocaleString()} isRate={false}
                rows={rows} shortMonth={shortMonth}
                delta={deltaByMetric[c.key]} />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {rates.map((r) => (
          <SalesContextMini key={r.key} label={r.label} dataKey={r.key} color={r.color}
                fmt={(v) => `${v.toFixed(1)}%`} isRate={true}
                rows={rows} shortMonth={shortMonth}
                delta={deltaByMetric[r.key]} />
        ))}
      </div>
    </>
  );
}

/**
 * PlatformMetricTile — a Dashboard-style tile for one platform metric.
 * Big value · MoM chip · YoY chip · 12-month line spark. Matches the
 * visual language of DashboardPage's MonthTile so the two pages read the same.
 */
function PlatformMetricTile({ testid, label, block, fmt, invert = false, color = "#1D57A5" }) {
  if (!block) return null;
  const series = (block.series || []).filter((r) => r.value != null);
  const shortMonth = (p) => {
    if (!p || p.length < 7) return p;
    const [y, m] = p.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
  };
  return (
    <div className="bg-warm border-t-4 border-cobalt p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{label}</div>
      <div className="text-2xl font-bold text-navy mb-1">
        {block.value != null ? fmt(block.value) : "—"}
      </div>
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <MomChip pct={block.delta_pct_mom} invert={invert} testid={`${testid}-mom`} />
        {block.delta_pct_yoy != null
          ? <YoyBadge pct={block.delta_pct_yoy} invert={invert} testid={`${testid}-yoy`} />
          : <span className="text-[10px] uppercase tracking-widest opacity-40" data-testid={`${testid}-yoy-na`}>YoY n/a</span>}
      </div>
      {series.length > 1 && (
        <ResponsiveContainer width="100%" height={56}>
          <LineChart data={series} margin={{ top: 4, right: 6, bottom: 2, left: 2 }}>
            <XAxis dataKey="period" tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.55 }}
                   tickFormatter={shortMonth} axisLine={false} tickLine={false}
                   interval="preserveStartEnd" minTickGap={30} />
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Tooltip formatter={(v) => v == null ? "—" : fmt(v)} labelFormatter={shortMonth}
              contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
              itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.75}
                  dot={false} connectNulls={true} isAnimationActive={false}
                  activeDot={{ r: 3, fill: color, stroke: "#FFD100", strokeWidth: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/**
 * RollingRoasSpark — single line chart showing the trailing 3-month ROAS
 * average per platform vs the breakeven reference. No daily granularity per
 * Joey's directive.
 */
function RollingRoasSpark({ series, breakeven, color = "#1D57A5", testid }) {
  const rows = (series || []).map((r) => ({ period: r.period, roas: r.value }));
  if (!rows.length) return null;
  const shortMonth = (p) => {
    if (!p || p.length < 7) return p;
    const [y, m] = p.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
  };
  return (
    <div className="mt-5 bg-warm border-t-4 border-cobalt p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-sm font-bold text-navy uppercase tracking-widest">Rolling 3-month ROAS</h4>
        <span className="text-[10px] uppercase tracking-widest opacity-60">
          Breakeven = <strong className="text-navy">{breakeven}x</strong>
        </span>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
          <XAxis dataKey="period" tick={{ fill: "#101820", fontSize: 10 }}
                 tickFormatter={shortMonth} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: "#101820", fontSize: 10 }}
                 tickFormatter={(v) => `${(+v).toFixed(1)}x`}
                 domain={[
                   (dataMin) => Math.max(0, Math.floor((Math.min(dataMin, breakeven ? breakeven - 0.3 : dataMin)) * 10) / 10),
                   (dataMax) => Math.ceil((Math.max(dataMax, breakeven ? breakeven + 0.3 : dataMax)) * 10) / 10,
                 ]} />
          <Tooltip formatter={(v) => v == null ? "—" : `${(+v).toFixed(2)}x`}
                   labelFormatter={shortMonth}
                   contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
                   itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
          {breakeven && (
            <ReferenceLine y={breakeven} stroke="#141B4D" strokeDasharray="4 4" strokeWidth={1}
                           label={{ value: `Breakeven ${breakeven}x`, fill: "#141B4D", fontSize: 10, position: "insideTopRight" }} />
          )}
          <Line type="monotone" dataKey="roas" stroke={color} strokeWidth={2}
                dot={{ r: 3, fill: color }} connectNulls={true} isAnimationActive={false}
                activeDot={{ r: 4, fill: color, stroke: "#FFD100", strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * RailtownStrip — small supplementary context row showing click-quality
 * metrics from Railtown for the platform. Purely informational — a spike in
 * CPC or drop in CTR often precedes CPL moves by ~2 weeks.
 */
function RailtownStrip({ block, platform }) {
  if (!block) return null;
  const platKey = platform.toLowerCase().replace(/\s+/g, "-");
  const shortMonth = (p) => {
    if (!p || p.length < 7) return p;
    const [y, m] = p.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
  };
  return (
    <div className="mt-4 bg-warm border-l-4 border-cobalt/60 p-3" data-testid={`railtown-strip-${platKey}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-cobalt font-bold">
          Traffic quality · Railtown · {shortMonth(block.period)}
        </div>
        <div className="text-[10px] opacity-50">click-level, monthly</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">Clicks</div>
          <div className="font-bold text-navy">{block.clicks?.value?.toLocaleString() || "—"}</div>
          <MomChip pct={block.clicks?.delta_pct_mom} testid={`railtown-${platKey}-clicks-mom`} />
        </div>
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">CTR</div>
          <div className="font-bold text-navy">{block.ctr?.value != null ? `${(block.ctr.value * 100).toFixed(2)}%` : "—"}</div>
          <MomChip pct={block.ctr?.delta_pct_mom} testid={`railtown-${platKey}-ctr-mom`} />
        </div>
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">CPC</div>
          <div className="font-bold text-navy">{block.cpc?.value != null ? fmtMoney(block.cpc.value) : "—"}</div>
          <MomChip pct={block.cpc?.delta_pct_mom} invert testid={`railtown-${platKey}-cpc-mom`} />
        </div>
      </div>
    </div>
  );
}

/**
 * ReconcileStrip — tiny warning bar when Railtown's spend disagrees with BHAG
 * by more than 2% for the latest full month. Follows the same rule as Slack
 * reconciliation: BHAG is authoritative, Railtown is shown for transparency.
 */
function ReconcileStrip({ block, platform }) {
  if (!block || !block.mismatch) return null;
  const sign = (block.delta_pct || 0) > 0 ? "+" : "";
  const platKey = platform.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="mt-3 text-[10px] uppercase tracking-widest flex items-center gap-2 text-red-700"
         data-testid={`reconcile-strip-${platKey}`}>
      <AlertTriangle className="w-3 h-3" />
      <span>Railtown spend {fmtMoney(block.railtown_spend)} vs BHAG {fmtMoney(block.bhag_spend)} · {sign}{block.delta_pct?.toFixed(1)}% delta · BHAG used</span>
    </div>
  );
}

const VERDICT_COLOR_CLS = {
  evergreen: "border-evergreen bg-warm-tint",
  cobalt:    "border-cobalt bg-warm-tint",
  yellow:    "border-yellow bg-warm-tint",
  red:       "border-red-700 bg-warm-tint",
};
const VERDICT_TEXT_CLS = {
  evergreen: "text-evergreen",
  cobalt:    "text-cobalt",
  yellow:    "text-orange-700",
  red:       "text-red-700",
};

function PlatformRecommendationCard({ p, breakeven, recoWindow, monthly }) {
  const v = p.verdict || {};
  const insufficient = v.label === "Insufficient data";
  const at20 = (p.scenarios || []).find((s) => s.scale_pct === 20);
  const at0  = (p.scenarios || []).find((s) => s.scale_pct === 0);
  const platformKey = p.platform.toLowerCase().replace(/\s+/g, "-");
  const platColor = platformKey.includes("meta") ? "#F58518" : "#1D57A5";
  return (
    <div className={`p-6 border-t-4 ${VERDICT_COLOR_CLS[v.color] || "border-cobalt bg-warm-tint"}`}
         data-testid={`scaling-card-${platformKey}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-xl font-bold text-navy">{p.platform}</h3>
        <span className={`text-xs uppercase tracking-widest font-bold ${VERDICT_TEXT_CLS[v.color] || "text-cobalt"}`}
              data-testid={`scaling-verdict-${platformKey}`}>
          {v.label}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">{v.headline}</p>

      {/* MoM/YoY tile grid — Dashboard-style, sourced from BHAG. */}
      {monthly && (
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <PlatformMetricTile testid={`tile-${platformKey}-spend`}
                              label="Ad Spend" block={monthly.spend}
                              fmt={fmtMoney} color={platColor} />
          <PlatformMetricTile testid={`tile-${platformKey}-calls`}
                              label="Calls Booked" block={monthly.calls_booked}
                              fmt={(v) => fmtNumber(v, 0)} color={platColor} />
          <PlatformMetricTile testid={`tile-${platformKey}-cpl`}
                              label="CPL · spend ÷ calls" block={monthly.cpl}
                              fmt={fmtMoney} invert color={platColor} />
          <PlatformMetricTile testid={`tile-${platformKey}-roas`}
                              label="ROAS" block={monthly.roas}
                              fmt={fmtMultiple} color={platColor === "#F58518" ? "#F58518" : "#006747"} />
        </div>
      )}

      {/* Rolling 3-mo ROAS trendline vs breakeven */}
      {monthly && <RollingRoasSpark series={monthly.rolling_roas_3mo}
                                    breakeven={breakeven} color={platColor}
                                    testid={`rolling-roas-${platformKey}`} />}

      {/* Railtown click-quality strip + reconciliation warning */}
      {monthly && <RailtownStrip block={monthly.railtown} platform={p.platform} />}
      {monthly && <ReconcileStrip block={monthly.reconcile} platform={p.platform} />}

      {insufficient ? (
        <div className="mt-4 text-xs text-midnight opacity-70 italic">
          HubSpot has no closed-won deals attributed to {p.platform} in the 30-day scaling window (checked {p.hubspot_sources_used?.join(" / ") || "PAID_SEARCH / PAID_SOCIAL"}). The tiles above still show BHAG&apos;s monthly actuals for this platform — scenarios are hidden until attribution catches up.
        </div>
      ) : (
        <>
          {/* Joey's default move comparison */}
          <div className="mt-5 bg-warm border-l-4 border-navy p-4">
            <div className="text-[10px] uppercase tracking-widest text-cobalt mb-2">Your default move next month: +20%</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="opacity-60">Spend next</div>
                <div className="font-bold text-navy text-base">{fmtMoney(at20?.next_spend)}</div>
                <div className="text-[10px] opacity-50">vs {fmtMoney(at0?.next_spend)} flat</div>
              </div>
              <div>
                <div className="opacity-60">Projected CPL</div>
                <div className="font-bold text-navy text-base">{fmtMoney(at20?.projected_cpl)}</div>
                <div className="text-[10px] opacity-50">+{(((at20?.projected_cpl || 0) / (at0?.projected_cpl || 1) - 1) * 100).toFixed(0)}% inflation</div>
              </div>
              <div>
                <div className="opacity-60">Projected revenue</div>
                <div className="font-bold text-evergreen text-base">{fmtMoney(at20?.next_revenue)}</div>
              </div>
              <div>
                <div className="opacity-60">Projected ROAS</div>
                <div className={`font-bold text-base ${(at20?.projected_roas || 0) >= breakeven ? "text-evergreen" : "text-red-700"}`}
                     data-testid={`scaling-projected-roas-${platformKey}`}>
                  {fmtMultiple(at20?.projected_roas)}
                </div>
                <div className="text-[10px] opacity-50">breakeven {fmtMultiple(breakeven)}</div>
              </div>
            </div>
          </div>

          {/* All scenarios table */}
          <table className="mt-5 il-table w-full text-xs" data-testid={`scaling-scenarios-${platformKey}`}>
            <thead>
              <tr>
                <th>Scale</th><th>Spend</th><th>Proj CPL</th><th>Leads</th><th>Deals</th><th>Revenue</th><th>ROAS</th>
              </tr>
            </thead>
            <tbody>
              {(p.scenarios || []).map((s) => {
                const isRec = s.scale_pct === v.recommended_scale_pct;
                const isDefault = s.scale_pct === 20;
                return (
                  <tr key={s.scale_pct}
                      data-testid={`scaling-row-${platformKey}-${s.scale_pct}`}
                      style={isRec ? { outline: "2px solid #006747", background: "#F2E9DB" } : {}}>
                    <td>
                      <strong className={isRec ? "text-evergreen" : "text-navy"}>
                        {s.scale_pct > 0 ? "+" : ""}{s.scale_pct}%
                      </strong>
                      {isRec && <span className="ml-2 text-[9px] uppercase tracking-widest text-evergreen">Recommended</span>}
                      {isDefault && !isRec && <span className="ml-2 text-[9px] uppercase tracking-widest text-cobalt opacity-70">Your default</span>}
                    </td>
                    <td>{fmtMoney(s.next_spend)}</td>
                    <td>{fmtMoney(s.projected_cpl)}</td>
                    <td>{fmtNumber(s.next_leads, 0)}</td>
                    <td>{fmtNumber(s.next_deals, 1)}</td>
                    <td className="text-evergreen">{fmtMoney(s.next_revenue)}</td>
                    <td className={(s.projected_roas || 0) >= breakeven ? "text-evergreen font-bold" : "text-red-700 font-bold"}>
                      {fmtMultiple(s.projected_roas)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default function ConsolePage() {
  const [pacing, setPacing] = useState(null);
  const [reconcile, setReconcile] = useState(null);
  const [target, setTarget] = useState(null);
  const [targetMonth, setTargetMonth] = useState(null);
  const [recoWindow] = useState(30);  // fixed at 30d — 60/90 removed per Joey
  const [scaling, setScaling] = useState(null);
  const [salesCtx, setSalesCtx] = useState(null);
  const [platMonthly, setPlatMonthly] = useState(null);
  const [recoActions, setRecoActions] = useState({});  // keyed by campaign_id

  // Bootstrap — pull the CURRENT month's goals directly from BHAG (revenue &
  // sales targets are in monthly_targets, which mirrors row 3 / 8 of the
  // '2026 Indie Law NEW' tab). No user input needed.
  useEffect(() => {
    const yr = new Date().getFullYear();
    const mo = new Date().getMonth();  // 0-indexed → we want CURRENT month name
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    api.get(`/targets/monthly?year=${yr}`).then((r) => {
      const rows = r.data?.months || [];
      const row = rows.find((m) => m.month_name === monthNames[mo])
                || rows[rows.length - 1] || {};
      setTargetMonth(row);
      setTarget({
        revenue: row.revenue_target || 260000,
        deals: row.sales_target || 98,
      });
    }).catch(() => setTarget({ revenue: 260000, deals: 98 }));
  }, []);

  useEffect(() => {
    if (!target) return;
    api.get(`/analytics/pacing?target_revenue=${target.revenue}&target_deals=${target.deals}`)
      .then((r) => setPacing(r.data));
  }, [target?.revenue, target?.deals]);

  useEffect(() => {
    api.get("/reconciliation").then((r) => setReconcile(r.data)).catch(() => {});
    api.get("/analytics/sales-context-monthly?months=12")
      .then((r) => setSalesCtx(r.data)).catch(() => {});
    api.get("/analytics/platform-monthly?months=12")
      .then((r) => setPlatMonthly(r.data)).catch(() => {});
    reloadActions();
  }, []);

  useEffect(() => {
    api.get(`/analytics/scaling-recommendation?days=${recoWindow}`)
      .then((r) => setScaling(r.data));
  }, [recoWindow]);

  function reloadActions() {
    api.get("/analytics/recovery-actions")
      .then((r) => {
        const map = {};
        (r.data?.actions || []).forEach((a) => { map[a.campaign_id] = a; });
        setRecoActions(map);
      })
      .catch(() => {});
  }

  async function applyAction(campaign_id, action, extra = {}) {
    try {
      await api.post("/analytics/recovery-action", { campaign_id, action, ...extra });
      reloadActions();
    } catch (e) { /* silent — UI will not update */ }
  }

  if (!pacing || !target) return <div className="p-10 text-navy">Loading recovery console…</div>;

  const { mtd, gap, projected, days_elapsed, days_in_month, days_remaining, funnel, reallocation, data_sources } = pacing;
  const slackMtd = data_sources?.slack_mtd;
  const onTrack = projected.on_track;
  const paceDeltaPct = pacing.target_revenue > 0 ? (projected.revenue - pacing.target_revenue) / pacing.target_revenue : 0;

  // Part 3's verdict is now ROAS-driven (from the pacing endpoint), so we no
  // longer compute best/worst CPL buckets on the client.

  const breakeven = scaling?.assumptions?.breakeven_roas ?? 1.18;

  // Deep-link builders for the "open in Ads Manager" icon on each row.
  const adsManagerHref = (platform) => {
    if ((platform || "").toLowerCase().includes("google")) return "https://ads.google.com/aw/campaigns";
    if ((platform || "").toLowerCase().includes("meta")) return "https://adsmanager.facebook.com/adsmanager";
    return null;
  };

  const monthlyByPlatform = {
    "Google Ads": platMonthly?.google,
    "Meta Ads":   platMonthly?.meta,
  };

  return (
    <div data-testid="console-page">
      <TitleBlock
        eyebrow="This month + next month · operating console"
        title="Console"
        subtitle="Two questions this page answers: (1) are we making this month? and (2) given how Google + Meta are performing, should we still scale +20% next month, hold, or pull back?"
        right={
          targetMonth && (
            <div className="text-right" data-testid="recovery-bhag-badge">
              <div className="text-[10px] uppercase tracking-widest opacity-70">
                {targetMonth.month_name} goal · BHAG sheet
              </div>
              <div className="text-sm font-bold text-navy mt-0.5">
                {fmtMoney(target?.revenue)} · {fmtNumber(target?.deals, 0)} sales
              </div>
            </div>
          )
        }
      />

      <div className="p-6 md:p-8 space-y-10">

        {/* ============================== */}
        {/* OWNERSHIP CALLOUT              */}
        {/* ============================== */}
        <OwnershipCallout salesCtx={salesCtx} />

        {/* ============================== */}
        {/* PART 1 — This month's pacing   */}
        {/* ============================== */}
        <section>
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-baseline gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold">Part 1 · This month</span>
              <span className="text-xs opacity-50">Are we going to make it?</span>
            </div>
            {slackMtd?.available && (
              <div className="text-right text-[10px] uppercase tracking-widest" data-testid="slack-mtd-badge">
                <div className="opacity-60">Source · authoritative MTD through {slackMtd.as_of_month} {slackMtd.as_of_day}</div>
                <div className="text-cobalt font-bold">
                  Slack {slackMtd.channel} · today excluded
                </div>
              </div>
            )}
          </div>
          <div className={`p-6 border-t-4 ${onTrack ? "border-evergreen bg-warm-tint" : "border-yellow"}`}
               style={!onTrack ? { background: "#FBF5EA" } : {}}
               data-testid="pacing-hero">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                  {onTrack ? (
                    <span className="text-evergreen inline-flex items-center gap-1" data-testid="on-track-badge"><CheckCircle2 className="w-4 h-4" /> On track</span>
                  ) : (
                    <span className="text-yellow inline-flex items-center gap-1 bg-navy px-2 py-0.5" data-testid="off-track-badge"><AlertTriangle className="w-4 h-4" /> Off track</span>
                  )}
                  <span className="text-midnight opacity-60">Through day {days_elapsed} of {days_in_month} · today not yet closed</span>
                </div>
                <div className="mt-3 text-3xl md:text-4xl font-bold text-navy">
                  {onTrack ? "Pacing ahead of target." : `Behind by ${fmtMoney(gap.revenue)}.`}
                </div>
                <div className="text-sm mt-2 text-midnight opacity-80 max-w-xl">
                  {onTrack
                    ? `Projected month-end: ${fmtMoney(projected.revenue)} (${fmtNumber(projected.deals, 1)} sales). You're ${fmtPercent(Math.max(0, paceDeltaPct))} above target.`
                    : `To make it up, close ${fmtNumber(gap.daily_deals_needed, 1)} sales/day for the next ${days_remaining} days — that's ${fmtMoney(gap.daily_revenue_needed)}/day vs your current pace of ${fmtMoney(gap.daily_revenue_pace_now)}/day.`}
                </div>
              </div>
              {!onTrack && <ArrowDownRight className="w-16 h-16 text-red-700 shrink-0" />}
              {onTrack && <ArrowUpRight className="w-16 h-16 text-evergreen shrink-0" />}
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="kpi-tile money" data-testid="kpi-mtd-revenue">
                <div className="text-xs uppercase tracking-[0.2em] opacity-70">MTD Revenue</div>
                <div className="text-2xl font-bold text-evergreen mt-2">{fmtMoney(mtd.revenue)}</div>
                <div className="text-[10px] text-midnight opacity-60 mt-1">{mtd.closed_deals} sales</div>
                <ReconcileFlag row={findReconcile(reconcile, "sales")} compact testid="recovery-reconcile-sales" />
              </div>
              <KpiTile testid="kpi-projected" label="Projected month-end" value={fmtMoney(projected.revenue)} sub={`${fmtNumber(projected.deals, 1)} sales`} />
              <KpiTile testid="kpi-gap" label="Revenue gap" value={fmtMoney(gap.revenue)} sub={`${fmtNumber(gap.deals, 1)} sales to go`} variant="accent" />
              <KpiTile testid="kpi-daily-needed" label="Daily $ needed" value={fmtMoney(gap.daily_revenue_needed)} sub={`vs ${fmtMoney(gap.daily_revenue_pace_now)} now`} />
              <KpiTile testid="kpi-days-left" label="Days remaining" value={fmtNumber(days_remaining)} sub={`incl. today · of ${days_in_month}`} />
            </div>
            {slackMtd?.available && (
              <div className="mt-4 text-[10px] uppercase tracking-widest opacity-60 flex flex-wrap items-center gap-x-4 gap-y-1"
                   data-testid="slack-mtd-detail">
                <span>Slack MTD read:</span>
                <span>Booked <strong className="text-navy">{fmtNumber(slackMtd.calls_booked_mtd, 0)}</strong></span>
                <span>Held <strong className="text-navy">{fmtNumber(slackMtd.calls_had_mtd, 0)}</strong></span>
                <span>Qualified <strong className="text-navy">{fmtNumber(slackMtd.qual_calls_mtd, 0)}</strong></span>
                <span>Sales <strong className="text-navy">{fmtNumber(slackMtd.sales_mtd, 0)}</strong></span>
                <span>Revenue <strong className="text-navy">{fmtMoney(slackMtd.revenue_mtd)}</strong></span>
              </div>
            )}
          </div>
        </section>

        {/* ============================== */}
        {/* PART 2 — Next-month scaling    */}
        {/* ============================== */}
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold">Part 2 · Next month</span>
            <span className="text-xs opacity-50">Should we still scale +20%? · 30-day read</span>
          </div>

          <Callout title="How this works" testid="scaling-method-callout">
            <div className="text-xs">
              We look at <strong>BHAG&apos;s monthly per-platform actuals</strong> — same denominator as Joey&apos;s operating scorecard. &quot;Lead&quot; is defined the same way for both platforms: <strong>Calls Booked</strong> from that platform (never webinar registrants). Each tile shows the last completed month with MoM &amp; YoY deltas, and the trendline below is a rolling 3-month ROAS vs your breakeven of <strong>{breakeven}x</strong> (at {fmtPercent(scaling?.assumptions?.profit_margin || 0.15)} margin).
            </div>
            <div className="text-[10px] mt-2 opacity-70 italic flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Click-quality context (Clicks / CTR / CPC) comes from Railtown&apos;s monthly report. When Railtown&apos;s spend disagrees with BHAG we surface the mismatch, but BHAG stays authoritative.</span>
            </div>
          </Callout>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {(scaling?.platforms || []).map((p) => (
              <PlatformRecommendationCard key={p.platform} p={p} breakeven={breakeven}
                                          recoWindow={recoWindow}
                                          monthly={monthlyByPlatform[p.platform]} />
            ))}
          </div>
        </section>

        {/* ============================== */}
        {/* PART 3 — Paid channel allocation */}
        {/* ============================== */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-baseline gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold">Part 3 · Paid channel allocation</span>
              <span className="text-xs opacity-50">Where the next dollar should go · this month</span>
            </div>
            <span className="text-xs uppercase tracking-widest text-cobalt">Ranked by ROAS · high to low</span>
          </div>

          {/* Purpose banner — clarifies that this section is a decision-support
              triage view, not an ads-account execution surface. */}
          <div className="bg-warm border-l-4 border-cobalt p-4 mb-4 text-xs text-midnight" data-testid="reallocation-purpose">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cobalt mb-1">What this is for</div>
            <p className="opacity-80">
              A weekly triage view ranking Google and Meta by <strong>ROAS</strong> (revenue ÷ spend, from BHAG). Rows are labeled <strong>Double down</strong> at ≥ 1.3×, <strong>Pause / reallocate</strong> below 1.0×, and <strong>Hold</strong> otherwise. Actions here <strong>don&apos;t change your ads accounts</strong> — use the <ExternalLink className="w-3 h-3 inline align-baseline" /> icons to open the corresponding Ads Manager and make the change there. <strong>Acknowledge</strong> when you&apos;ve acted; <strong>Snooze</strong> to hide for a week; <strong>Dismiss</strong> if it&apos;s a bad read.
            </p>
          </div>

          <table className="il-table w-full" data-testid="reallocation-table">
            <thead>
              <tr><th></th><th>Platform</th><th>Status</th><th>Spend (MTD)</th><th>Calls booked</th><th>CPL</th><th>ROAS</th><th>Recommendation</th><th>Action</th></tr>
            </thead>
            <tbody>
              {reallocation.map((r, idx) => {
                const rowAction = recoActions[r.campaign_id];
                const isDismissed = rowAction && rowAction.action === "dismiss";
                const isSnoozed = rowAction && rowAction.action === "snooze";
                const isAcked = rowAction && rowAction.action === "acknowledge";
                const href = adsManagerHref(r.platform);
                const vKey = r.verdict?.key || "hold";
                const vColor = r.verdict?.color || "cobalt";
                const outlineColor = vKey === "double_down" ? "#006747"
                                   : vKey === "pause" ? "#b91c1c"
                                   : vKey === "hold" ? "#F58518" : null;
                const rowStyle = (isDismissed || isSnoozed)
                  ? { opacity: 0.45 }
                  : outlineColor ? { outline: `2px solid ${outlineColor}` } : {};
                const roasCls = vKey === "double_down" ? "text-evergreen font-bold"
                              : vKey === "pause" ? "text-red-700 font-bold"
                              : "text-navy font-bold";
                return (
                  <tr key={r.campaign_id} data-testid={`reallocation-row-${r.campaign_id}`}
                      style={rowStyle}>
                    <td className="w-8 text-center font-bold opacity-50">{idx + 1}</td>
                    <td>
                      <strong className="text-navy">{r.campaign_name}</strong>
                      {href && (
                        <a href={href} target="_blank" rel="noopener noreferrer"
                           className="ml-2 inline-flex items-center text-cobalt hover:text-navy"
                           title={`Open in ${r.platform} Ads Manager`}
                           data-testid={`reallocation-deeplink-${r.campaign_id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {isAcked && <div className="text-[9px] uppercase tracking-widest text-evergreen mt-0.5">Acknowledged</div>}
                      {isSnoozed && <div className="text-[9px] uppercase tracking-widest text-cobalt mt-0.5">Snoozed until {(rowAction.snooze_until || "").slice(0, 10)}</div>}
                      {isDismissed && <div className="text-[9px] uppercase tracking-widest text-red-700 mt-0.5">Dismissed</div>}
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest ${r.status === "Active" ? "bg-evergreen text-warm" : "bg-yellow text-midnight"}`}>{r.status}</span>
                    </td>
                    <td>{fmtMoney(r.spend)}</td>
                    <td>{fmtNumber(r.leads, 0)}</td>
                    <td>{r.cpl != null ? fmtMoney(r.cpl) : "—"}</td>
                    <td className={roasCls} data-testid={`reallocation-roas-${r.campaign_id}`}>
                      {r.roas != null ? fmtMultiple(r.roas) : "—"}
                    </td>
                    <td data-testid={`reallocation-verdict-${r.campaign_id}`}>
                      {vKey === "double_down" && <span className="inline-flex items-center gap-1 text-evergreen text-xs font-bold uppercase tracking-wider"><TrendingUp className="w-3.5 h-3.5" /> Double down</span>}
                      {vKey === "pause" && <span className="inline-flex items-center gap-1 text-red-700 text-xs font-bold uppercase tracking-wider"><TrendingDown className="w-3.5 h-3.5" /> Pause / reallocate</span>}
                      {vKey === "hold" && <span className="inline-flex items-center gap-1 text-yellow text-xs font-bold uppercase tracking-wider bg-navy px-2 py-0.5">Hold</span>}
                      {vKey === "insufficient" && <span className="text-xs opacity-50">{r.verdict?.label || "—"}</span>}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {rowAction ? (
                          <button type="button"
                                  onClick={() => applyAction(r.campaign_id, "clear")}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest bg-warm border border-navy text-navy hover:bg-navy hover:text-warm"
                                  data-testid={`reco-clear-${r.campaign_id}`}
                                  title="Clear action">
                            <XIcon className="w-3 h-3" /> Undo
                          </button>
                        ) : (
                          <>
                            <button type="button"
                                    onClick={() => applyAction(r.campaign_id, "acknowledge")}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest bg-evergreen text-warm hover:opacity-80"
                                    data-testid={`reco-ack-${r.campaign_id}`}
                                    title="I've acted on this">
                              <Check className="w-3 h-3" /> Ack
                            </button>
                            <button type="button"
                                    onClick={() => applyAction(r.campaign_id, "snooze", { snooze_days: 7 })}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest bg-cobalt text-warm hover:opacity-80"
                                    data-testid={`reco-snooze-${r.campaign_id}`}
                                    title="Hide for 7 days">
                              <Clock className="w-3 h-3" /> 7d
                            </button>
                            <button type="button"
                                    onClick={() => applyAction(r.campaign_id, "dismiss")}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest bg-warm border border-red-700 text-red-700 hover:bg-red-700 hover:text-warm"
                                    data-testid={`reco-dismiss-${r.campaign_id}`}
                                    title="Bad read — hide this cycle">
                              <XIcon className="w-3 h-3" /> Dismiss
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {reallocation.length === 0 && (
                <tr><td colSpan={9} className="text-center opacity-60">No campaign data this month yet.</td></tr>
              )}
            </tbody>
          </table>
        </section>

        {/* ============================== */}
        {/* PART 4 — Sales-side context    */}
        {/* ============================== */}
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold">Sales-side context</span>
            <span className="text-xs opacity-50">Not levers you control — but worth knowing · 12-month trend</span>
          </div>
          <div className="bg-warm border-l-4 border-cobalt p-5 text-sm" data-testid="sales-context">
            <div className="text-xs text-midnight opacity-70 italic mb-4">
              Show-up rate and close rate sit with the sales team — they don&apos;t change anything in this dashboard. Tracked here as trend lines so you can see which parts of the funnel are drifting over time. Each tile shows the last completed month plus MoM and YoY deltas (where 2025 data exists).
            </div>
            <SalesContextTrends data={salesCtx} />
          </div>
        </section>
      </div>
    </div>
  );
}
